import React, { useState, useRef, useEffect } from 'react';
import { useAnalytics } from '../../../context/AnalyticsContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { isCodexUi } from '../../../utils/codexEnv';
import AnalyticsPipelineSettingsModal from './AnalyticsPipelineSettingsModal';

/**
 * AnalyticsPipelineSelector - allows users to switch between analytics pipelines
 * Similar to PipelineSelector but for Analytics pipelines
 */
const AnalyticsPipelineSelector = ({ subscriptionStatus }) => {
  const {
    pipelines,
    activePipeline,
    loading,
    switchPipeline,
    createNewPipeline,
    deletePipelineById,
    duplicatePipelineById,
  } = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [error, setError] = useState(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get pipeline limit from subscription (default to 3 for analytics)
  const maxPipelines = subscriptionStatus?.maxAnalyticsPipelines ?? 3;
  const currentCount = pipelines?.length || 0;
  const canCreate = maxPipelines === -1 || currentCount < maxPipelines;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsCreating(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = async (pipelineId) => {
    if (pipelineId === activePipeline?.id) {
      setIsOpen(false);
      return;
    }
    await switchPipeline(pipelineId);
    setIsOpen(false);
  };

  const handleCreateClick = () => {
    setIsCreating(true);
    setNewPipelineName('');
    setError(null);
  };

  const handleCreateSubmit = async () => {
    if (!newPipelineName.trim()) {
      setError('Name is required');
      return;
    }

    if (pipelines.some(p => p.name.toLowerCase() === newPipelineName.trim().toLowerCase())) {
      setError('A pipeline with this name already exists');
      return;
    }

    const result = await createNewPipeline(newPipelineName.trim());
    if (result) {
      setIsCreating(false);
      setNewPipelineName('');
      setError(null);
    } else {
      setError('Failed to create pipeline');
    }
  };

  const handleDeleteClick = async (e, pipelineId) => {
    e.stopPropagation();
    if (pipelineId === activePipeline?.id) {
      setError('Cannot delete the active pipeline');
      return;
    }
    const success = await deletePipelineById(pipelineId);
    if (!success) {
      setError('Failed to delete pipeline');
    }
  };

  const handleDuplicateClick = async (e, pipeline) => {
    e.stopPropagation();
    if (!canCreate) {
      setError(`Pipeline limit reached (${maxPipelines})`);
      return;
    }
    const newName = `${pipeline.name} (copy)`;
    const result = await duplicatePipelineById(pipeline.id, newName);
    if (!result) {
      setError('Failed to duplicate pipeline');
    }
  };

  if (isCodexUi) {
    return null;
  }

  if (!activePipeline && !loading) {
    return null;
  }

  return (
    <div className="pipeline-selector" ref={dropdownRef}>
      <div className="pipeline-selector-row">
        <button
          className={`pipeline-selector-trigger ${isOpen ? 'open' : ''} ${loading ? 'loading' : ''}`}
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
        >
          <span className="pipeline-label">Pipeline:</span>
          <span className="pipeline-name">
            {loading ? 'Loading...' : activePipeline?.name || 'Default'}
          </span>
          {maxPipelines !== -1 && (
            <span className="pipeline-count">
              ({currentCount}/{maxPipelines})
            </span>
          )}
          <KeyboardArrowDownIcon className={`pipeline-arrow ${isOpen ? 'rotated' : ''}`} />
        </button>
        {activePipeline && (
          <button
            className="pipeline-settings-btn"
            onClick={() => setSettingsModalOpen(true)}
            title="Pipeline settings"
          >
            <SettingsIcon fontSize="small" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="pipeline-dropdown">
          {error && (
            <div className="pipeline-dropdown-error">{error}</div>
          )}

          <div className="pipeline-dropdown-list">
            {pipelines.map((pipeline) => (
              <div
                key={pipeline.id}
                className={`pipeline-dropdown-item ${pipeline.id === activePipeline?.id ? 'active' : ''}`}
                onClick={() => handleSelect(pipeline.id)}
              >
                <span className="pipeline-item-name">
                  {pipeline.name}
                  {pipeline.moduleCount !== undefined && (
                    <span className="pipeline-module-count">{pipeline.moduleCount} modules</span>
                  )}
                </span>
                <div className="pipeline-item-actions">
                  <button
                    className="pipeline-action-btn"
                    onClick={(e) => handleDuplicateClick(e, pipeline)}
                    title="Duplicate"
                    disabled={!canCreate}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </button>
                  {pipeline.id !== activePipeline?.id && pipelines.length > 1 && (
                    <button
                      className="pipeline-action-btn delete"
                      onClick={(e) => handleDeleteClick(e, pipeline.id)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {canCreate && (
            <div className="pipeline-dropdown-footer">
              {isCreating ? (
                <div className="pipeline-create-form">
                  <input
                    type="text"
                    value={newPipelineName}
                    onChange={(e) => setNewPipelineName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSubmit();
                      if (e.key === 'Escape') setIsCreating(false);
                    }}
                    placeholder="Pipeline name..."
                    autoFocus
                    className="pipeline-create-input"
                  />
                  <button
                    className="pipeline-create-btn save"
                    onClick={handleCreateSubmit}
                    title="Create"
                  >
                    <CheckIcon fontSize="small" />
                  </button>
                  <button
                    className="pipeline-create-btn cancel"
                    onClick={() => setIsCreating(false)}
                    title="Cancel"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ) : (
                <button className="pipeline-add-btn" onClick={handleCreateClick}>
                  <AddIcon fontSize="small" />
                  <span>New Pipeline</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settings Modal */}
      <AnalyticsPipelineSettingsModal
        pipeline={activePipeline}
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />
    </div>
  );
};

export default AnalyticsPipelineSelector;
